import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, ArrowRight, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { SOLUTIONS, SEO_DEFAULTS } from "@/data/content";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function DemoForm({ solution }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", role: "", message: "" });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/demo-requests", { ...form, solution: solution.slug });
      setSuccess(true);
      toast.success("Demo request received. Our team will be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  if (success) {
    return (
      <div className="bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-8 text-center" data-testid={`demo-success-${solution.slug}`}>
        <Check className="mx-auto text-gold" size={32} />
        <div className="font-display text-2xl text-[#001B54] mt-4">Request received.</div>
        <p className="text-[#3F4A6B] text-sm mt-3 leading-relaxed">
          A partner will contact you within one business day to schedule a private walkthrough of {solution.name}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-8 space-y-4" data-testid={`demo-form-${solution.slug}`}>
      <div className="overline">Request Private Demo · {solution.name}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required placeholder="Full name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="demo-input-name"
          className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
        <input required type="email" placeholder="Business email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="demo-input-email"
          className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
        <input placeholder="Company" value={form.company}
          onChange={(e) => setForm({ ...form, company: e.target.value })}
          data-testid="demo-input-company"
          className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
        <input placeholder="Role" value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          data-testid="demo-input-role"
          className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
      </div>
      <input placeholder="Phone (optional)" value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
        data-testid="demo-input-phone"
        className="w-full bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
      <textarea placeholder="Tell us about your priorities (optional)" rows={3} value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        data-testid="demo-input-message"
        className="w-full bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
      <button type="submit" disabled={sending} className="luxury-button w-full justify-center" data-testid="demo-submit">
        {sending ? <Loader2 size={14} className="animate-spin" /> : <>Request Demo <ArrowRight size={14} /></>}
      </button>
    </form>
  );
}

export default function SolutionDetail() {
  const { slug } = useParams();
  const solution = useMemo(() => SOLUTIONS.find((s) => s.slug === slug), [slug]);
  if (!solution) {
    return (
      <div className="pt-40 pb-24 px-6 text-center text-[#3F4A6B]">
        <Seo title="Platform not found" noindex />
        <div className="font-display text-3xl mb-4">Platform not found.</div>
        <Link to="/platforms" className="text-gold underline">Return to portfolio</Link>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: solution.name,
    description: solution.description,
    applicationCategory: "BusinessApplication",
    publisher: { "@type": "Organization", name: "AlchamisGroup", url: SEO_DEFAULTS.baseUrl },
  };

  return (
    <div data-testid={`solution-page-${solution.slug}`} className="pt-32 pb-24">
      <Seo
        title={solution.seo?.title || solution.name}
        description={solution.seo?.description || solution.mission}
        path={`/platforms/${solution.slug}`}
        image={solution.image}
        jsonLd={jsonLd}
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Link to="/platforms" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#6B7390] hover:text-gold">
          <ArrowLeft size={14} /> Back to Portfolio
        </Link>
        <div className="grid grid-cols-12 gap-8 mt-10 items-end">
          <div className="col-span-12 lg:col-span-7">
            <div className="number-badge">{solution.ventureCode}</div>
            <h1 className="font-display text-5xl sm:text-7xl text-[#001B54] mt-6 leading-[1.02]">
              {solution.name}
            </h1>
            <p className="text-gold/90 text-sm tracking-[0.18em] uppercase mt-4">{solution.tagline}</p>
            <p className="mt-8 text-[#3F4A6B] text-lg leading-relaxed max-w-2xl">{solution.mission}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#demo" className="luxury-button">Request Demo <ArrowRight size={14} /></a>
              <Link to="/contact" className="luxury-button-ghost">Speak to Partner</Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="aspect-[4/3] overflow-hidden border border-[#001B54]/10">
              <img src={solution.image} alt={solution.name} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="mt-24 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <span className="overline">Executive Summary</span>
          <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-[1.05]">
            Built for institutions
            <span className="block italic gold-text">that scale on trust.</span>
          </h2>
          <p className="text-[#3F4A6B] mt-6 leading-relaxed">{solution.description}</p>
        </div>
        <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#001B54]/8">
          {solution.pillars.map((p) => (
            <div key={p.title} className="bg-white p-8">
              <div className="font-display text-2xl text-[#001B54] mb-3">{p.title}</div>
              <p className="text-[#3F4A6B] text-sm leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {solution.feature_groups && (
        <section className="mt-24 max-w-[1400px] mx-auto px-6 md:px-12">
          <span className="overline">Features</span>
          <h2 className="font-display text-4xl text-[#001B54] mt-6 leading-tight">A complete platform.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#001B54]/8">
            {solution.feature_groups.map((g) => (
              <div key={g.group} className="bg-white p-8">
                <div className="overline mb-4">{g.group}</div>
                <ul className="space-y-2">
                  {g.items.map((it) => (
                    <li key={it} className="text-[#0A1228] text-sm flex items-start gap-2">
                      <Check size={14} className="text-gold mt-1 shrink-0" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {solution.screenshots && solution.screenshots.length > 0 && (
        <section className="mt-24 max-w-[1400px] mx-auto px-6 md:px-12">
          <span className="overline">Inside the Platform</span>
          <h2 className="font-display text-4xl text-[#001B54] mt-6 leading-tight">A glimpse of {solution.name}.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {solution.screenshots.map((src, idx) => (
              <div key={src} className="aspect-[16/10] overflow-hidden border border-[#001B54]/10 group">
                <img
                  src={src}
                  alt={`${solution.name} screenshot ${idx + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-75 group-hover:opacity-100 transition-opacity duration-700"
                />
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-24 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-6">
          <span className="overline">Use Cases</span>
          <h2 className="font-display text-3xl sm:text-4xl text-[#001B54] mt-6 leading-tight">When operators choose {solution.name}.</h2>
          <ul className="mt-8 space-y-4">
            {solution.use_cases.map((u, i) => (
              <li key={u} className="flex gap-4 items-start border-b border-[#001B54]/10 pb-4">
                <span className="number-badge mt-1">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[#0A1228] leading-relaxed">{u}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:col-start-8">
          <span className="overline">Industries Served</span>
          <h2 className="font-display text-3xl text-[#001B54] mt-6 leading-tight">Where {solution.name} operates.</h2>
          <div className="mt-8 grid grid-cols-2 gap-px bg-[#001B54]/8">
            {solution.industries.map((i) => (
              <div key={i} className="bg-white p-5 text-[#0A1228] text-sm">{i}</div>
            ))}
          </div>
          <div className="mt-12">
            <span className="overline">Outcomes</span>
            <div className="mt-6 space-y-5">
              {solution.metrics.map((m) => (
                <div key={m.label} className="border-b border-[#001B54]/10 pb-4">
                  <div className="font-display text-3xl gold-text">{m.value}</div>
                  <div className="text-[#3F4A6B] text-sm mt-1">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="demo" className="mt-24 max-w-[1100px] mx-auto px-6 md:px-12 scroll-mt-32">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <span className="overline">Engage</span>
            <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-tight">
              Request a private demonstration of {solution.name}.
            </h2>
            <p className="text-[#3F4A6B] mt-6 leading-relaxed">
              A solution architect will host a confidential walkthrough tailored to your operating model.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <DemoForm solution={solution} />
          </div>
        </div>
      </section>
    </div>
  );
}
